import type { CampaignScheduleSourceRecord } from '~/hooks/apis/offers/useGetSalesforceCampaignSchedules';

import { splitSalesforceMultiSelect } from '~/utils/splitSalesforceMultiSelect';

import type { CampaignScheduleDateRange, CampaignScheduleListVisibilityRow, CampaignSchedulePreview } from './types';

const DYNAMIC_CAMPAIGN_TYPE = {
  OFFLINE_PERIOD: 'Offline Period',
  LE_NEW_OFFER: 'LE – New Offer',
  LE_RE_OPEN: 'LE - Re-Open',
  LUX_PLUS_EXCLUSIVE: 'LuxPlus + Exclusive',
} as const;

const LIST_VISIBILITY_TYPES: Array<string> = [DYNAMIC_CAMPAIGN_TYPE.LE_NEW_OFFER, DYNAMIC_CAMPAIGN_TYPE.LE_RE_OPEN];

// Matches Campaign_Schedule__c.Brand__c picklist values directly in Salesforce to the svc-offer schedule `brand` slug.
// Only brands with a confirmed match in svc-offer's schedulesSchema.js brand enum are included here.
const BRAND_MAP: Record<string, string> = {
  LuxuryEscapes: 'luxuryescapes',
  'Scoopon AU': 'scoopontravel',
  Cudo: 'cudotravel',
  Deals: 'dealstravel',
  'TreatMe (NZ)': 'treatmetravel',
  'Business Traveller': 'lebusinesstraveller',
};

// Matches Campaign_Schedule__c.Market__c picklist values directly in Salesforce to lib-regions 2-letter codes.
// Countries with no matching LE distribution region (e.g. Switzerland, Cuba, Fiji) are intentionally left unmapped.
const MARKET_REGION_MAP: Record<string, string> = {
  Australia: 'AU',
  'New Zealand': 'NZ',
  India: 'IN',
  'United States': 'US',
  'Hong Kong': 'HK',
  'United Kingdom': 'GB',
  Singapore: 'SG',
  'South Africa': 'ZA',
  Thailand: 'TH',
  Indonesia: 'ID',
  China: 'CN',
  Taiwan: 'TW',
  Vietnam: 'VN',
  Japan: 'JP',
  Canada: 'CA',
  France: 'FR',
  Germany: 'DE',
  Ireland: 'IE',
  Israel: 'IL',
  Italy: 'IT',
  Korea: 'KR',
  Macau: 'MO',
  Malaysia: 'MY',
  Philippines: 'PH',
  Qatar: 'QA',
  Russia: 'RU',
  'Saudi Arabia': 'SA',
  Spain: 'ES',
  'United Arab Emirates': 'AE',
  'The Netherlands': 'NL',
};

function earliestStart(campaignSchedules: Array<CampaignScheduleSourceRecord>): string | null {
  const starts = campaignSchedules
    .flatMap((schedule) => [schedule.start_offline__c, schedule.start_online__c])
    .filter((value): value is string => !!value);

  if (starts.length === 0) {
    return null;
  }

  return starts.reduce((earliest, start) => (start < earliest ? start : earliest));
}

// Splits any multi-select picklist values and collects the distinct, non-empty entries verbatim as returned
// by the API, so the UI can display exactly what Salesforce sent rather than the mapped brand/region codes
// used for matching.
function distinctValues(rawValues: Array<string | null | undefined>): Array<string> {
  return Array.from(new Set(rawValues.flatMap((value) => splitSalesforceMultiSelect(value))));
}

export interface DeriveCampaignSchedulePreviewOptions {
  bookByEnd?: string | null;
  excludedBrands?: Array<string>;
}

export function deriveCampaignSchedulePreview(
  campaignSchedules: Array<CampaignScheduleSourceRecord>,
  { bookByEnd, excludedBrands = [] }: DeriveCampaignSchedulePreviewOptions,
): CampaignSchedulePreview {
  const warnings: Array<string> = [];

  const offlinePeriodSchedule = campaignSchedules.find(
    (schedule) => schedule.dynamic_campaign_type__c === DYNAMIC_CAMPAIGN_TYPE.OFFLINE_PERIOD,
  );
  const luxPlusSchedule = campaignSchedules.find(
    (schedule) => schedule.dynamic_campaign_type__c === DYNAMIC_CAMPAIGN_TYPE.LUX_PLUS_EXCLUSIVE,
  );
  const listVisibilitySchedules = campaignSchedules.filter((schedule) =>
    LIST_VISIBILITY_TYPES.includes(schedule.dynamic_campaign_type__c || ''),
  );

  const availabilityStart = earliestStart(campaignSchedules);
  const schedulesContributingToAvailability = campaignSchedules.filter(
    (schedule) => schedule.start_offline__c || schedule.start_online__c,
  );

  let availability: CampaignScheduleDateRange | null = null;

  if (bookByEnd && availabilityStart) {
    availability = {
      start: availabilityStart,
      end: bookByEnd,
      brands: distinctValues(schedulesContributingToAvailability.map((schedule) => schedule.brand__c)),
      markets: distinctValues(schedulesContributingToAvailability.map((schedule) => schedule.market__c)),
      campaignTypes: distinctValues(
        schedulesContributingToAvailability.map((schedule) => schedule.dynamic_campaign_type__c),
      ),
    };
  } else {
    if (!bookByEnd) {
      warnings.push('Opportunity.Book_By__c is not set — availability schedule cannot be derived');
    }
    if (!availabilityStart) {
      warnings.push('No campaign schedule has a start date set — availability schedule cannot be derived');
    }
  }

  let online_purchase: CampaignScheduleDateRange | null = null;

  if (offlinePeriodSchedule?.start_offline__c && offlinePeriodSchedule?.end_offline__c) {
    online_purchase = {
      start: offlinePeriodSchedule.start_offline__c,
      end: offlinePeriodSchedule.end_offline__c,
      brands: distinctValues([offlinePeriodSchedule.brand__c]),
      markets: distinctValues([offlinePeriodSchedule.market__c]),
      campaignTypes: distinctValues([offlinePeriodSchedule.dynamic_campaign_type__c]),
    };
  } else {
    warnings.push(
      "No 'Offline Period' campaign schedule with Start_Offline__c/End_Offline__c found — online_purchase schedule cannot be derived",
    );
  }

  const excludedBrandSet = new Set(excludedBrands);
  const unmappedBrands = new Set<string>();
  const unmappedMarkets = new Set<string>();

  const list_visibility: Array<CampaignScheduleListVisibilityRow> = listVisibilitySchedules
    .filter((schedule) => schedule.start_online__c && schedule.end_online__c)
    .flatMap((schedule) => {
      const brandValues = splitSalesforceMultiSelect(schedule.brand__c).filter((brand) => !excludedBrandSet.has(brand));
      const marketValues = splitSalesforceMultiSelect(schedule.market__c);

      return brandValues.flatMap((brandValue) => {
        const brand = BRAND_MAP[brandValue];

        if (!brand) {
          unmappedBrands.add(brandValue);
          return [];
        }

        return marketValues.flatMap((marketValue) => {
          const region = MARKET_REGION_MAP[marketValue];

          if (!region) {
            unmappedMarkets.add(marketValue);
            return [];
          }

          return [
            {
              brand__c: brandValue,
              market__c: marketValue,
              brand,
              region,
              brands: [brandValue],
              markets: [marketValue],
              campaignTypes: distinctValues([schedule.dynamic_campaign_type__c]),
              start: schedule.start_online__c,
              end: schedule.end_online__c,
            },
          ];
        });
      });
    });

  if (listVisibilitySchedules.length === 0) {
    warnings.push("No 'LE – New Offer' or 'LE - Re-Open' campaign schedules found — list_visibility cannot be derived");
  }

  unmappedBrands.forEach((brand) =>
    warnings.push(`Brand__c value "${brand}" has no known svc-offer brand mapping — skipped from list_visibility`),
  );
  unmappedMarkets.forEach((market) =>
    warnings.push(`Market__c value "${market}" has no known svc-offer region mapping — skipped from list_visibility`),
  );

  let lux_plus: CampaignScheduleDateRange | null = null;

  if (luxPlusSchedule?.start_online__c && luxPlusSchedule?.end_online__c) {
    lux_plus = {
      start: luxPlusSchedule.start_online__c,
      end: luxPlusSchedule.end_online__c,
      brands: distinctValues([luxPlusSchedule.brand__c]),
      markets: distinctValues([luxPlusSchedule.market__c]),
      campaignTypes: distinctValues([luxPlusSchedule.dynamic_campaign_type__c]),
    };
  }

  return {
    availability,
    online_purchase,
    list_visibility,
    lux_plus,
    warnings,
  };
}
